import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { constructSortFn } from '@tanstack/react-table';
import BusyOverlay from '../commonComponents/BusyOverlay';
import DataTable from '../data_table/DataTable';
import { sortByText } from '../data_table/sorting';
import Tooltip from '../ui/Tooltip';
import LimitTrack from './LimitTrack';
import RuleLimitInput from './RuleLimitInput';
import RuleStatusChip from './RuleStatusChip';
import {
    AttachedLimit,
    NotSet,
    RULE_TABLE_CLASS_NAME,
    RULE_TABLE_HEADER_HEIGHT,
    RuleValue,
    ruleColumns,
    ruleModeControl,
    ruleSwitchedOff,
} from './OptimizerRuleTableParts';
import {
    FACTOR_RULE,
    factorRuleId,
    factorRuleKey,
    formatRuleValue,
    ruleEditState,
} from './optimizerRuleRegistry';

const PLACEHOLDER_ROW_COUNT = 8;
const INITIAL_SORTING = Object.freeze([{ id: 'factor', desc: false }]);
const CONTRIBUTION_DESCRIPTION = "Each factor's signed share of factor volatility, relative to each portfolio's "
    + 'realized GMV. Read-only; limits do not constrain it.';

// Optimized values sort as numbers with a missing value counting as 0.
const sortByValueOrZero = constructSortFn({
    sort: (left, right) => (left ?? 0) - (right ?? 0),
});

function standaloneTooltip(evaluation, side) {
    const signed = evaluation?.detail?.[`${side}_signed`];
    const usd = evaluation?.detail?.[`${side}_usd`];
    if (signed == null || usd == null) return null;
    if (signed === 0) return 'No exposure';
    return `${signed < 0 ? 'Short' : 'Long'} exposure · ${formatRuleValue(usd, 'usd')}`;
}

function contributionTooltip(value) {
    return value == null ? null : `${formatRuleValue(value, 'usd')} of factor volatility`;
}

// Factors scope: a factor limit is attached, edited and switched off in the row
// where its result is shown. Standalone volatility is the constrained quantity;
// the contribution columns are read-only context measured on realized GMV.
// Rows start in factor order; the factor and both Optimized columns sort.
export default function ByFactorOptimizerRiskTable({
    factorNames,
    risksByFactor,
    constraints,
    evaluationIndex,
    baselineLabel,
    actions,
    resultStale = false,
    disabled = false,
    optimizerIsRunning,
    viewModeLoading,
    searchRanges = null,
    rangeEditor = null,
    modeEditor = null,
}) {
    const hasModeEditor = Boolean(modeEditor);
    const hasRangeEditor = Boolean(rangeEditor);
    const columns = useMemo(() => {
        const leaves = ruleColumns([
            {
                id: 'factor',
                header: 'Factor',
                width: 13,
                wrap: false,
                enableSorting: true,
                accessorFn: (row) => row.factor,
                sortFn: sortByText,
            },
            { id: 'mode', header: hasModeEditor ? 'Mode' : 'On', width: hasModeEditor ? 9 : 6, align: 'center' },
            {
                id: 'limit',
                header: hasRangeEditor ? 'Limit / range' : 'Limit',
                width: hasRangeEditor ? 20 : 14,
                align: 'right',
            },
            { id: 'baseline', header: baselineLabel, width: 9, align: 'right', wrap: false },
            {
                id: 'standalone',
                header: 'Optimized',
                width: 9,
                align: 'right',
                wrap: false,
                optimized: true,
                enableSorting: true,
                accessorFn: (row) => row.standalone,
                sortFn: sortByValueOrZero,
            },
            { id: 'track', header: 'Result vs limit', width: 16, align: 'right' },
            { id: 'status', header: 'Status', width: 14 },
            { id: 'contributionBaseline', header: baselineLabel, width: 9.5, align: 'right', wrap: false },
            {
                id: 'contribution',
                header: 'Optimized',
                width: 9.5,
                align: 'right',
                wrap: false,
                optimized: true,
                enableSorting: true,
                accessorFn: (row) => row.contribution,
                sortFn: sortByValueOrZero,
            },
        ], hasRangeEditor ? 1120 : 920);
        const [factor, mode, limit, baseline, standalone, track, status, contributionBaseline, contribution] = leaves;
        return [
            factor,
            mode,
            limit,
            {
                id: 'standaloneVol',
                header: `Standalone vol · ${FACTOR_RULE.unitLabel}`,
                meta: { description: FACTOR_RULE.description },
                columns: [baseline, standalone, track, status],
            },
            {
                id: 'contributionVol',
                header: 'Contribution to factor vol · bps of realized GMV',
                meta: { description: CONTRIBUTION_DESCRIPTION },
                columns: [contributionBaseline, contribution],
            },
        ];
    }, [baselineLabel, hasModeEditor, hasRangeEditor]);

    // In factor order, so the unsorted order is the default one.
    const factors = useMemo(
        () => [...factorNames].sort((left, right) => left.localeCompare(right)),
        [factorNames],
    );
    const rows = factors.map((factor) => {
        const risk = (risksByFactor || []).find((row) => row.factor === factor) || {};
        const evaluation = evaluationIndex.byRuleId.get(factorRuleId(factor));
        const record = constraints.dynamic_constraints?.[factorRuleKey(factor)] ?? null;
        const { changed, draftLimit } = ruleEditState(
            evaluation,
            record ? { enabled: record.enabled, value: record.value, explore: Boolean(searchRanges?.[`factor:${factor}`]) } : null,
        );
        const limitEditor = () => (rangeEditor ? rangeEditor({ parameter: `factor:${factor}`,
            value: record.value, unit: FACTOR_RULE.unit, form: FACTOR_RULE.form, label: factor, disabled,
            onChange: (value) => actions.setFactorRuleValue(factor, value),
        }) : (
            <RuleLimitInput
                value={record.value ?? null}
                onChange={(value) => actions.setFactorRuleValue(factor, value)}
                unit={FACTOR_RULE.unit}
                form={FACTOR_RULE.form}
                disabled={disabled}
                ariaLabel={`${factor} limit`}
                width={112}
            />
        ));
        return {
            id: factor,
            factor,
            standalone: evaluation?.optimized ?? null,
            contribution: risk.opt_factor_bps_euler ?? null,
            cells: {
                factor: (
                    <Tooltip content={factor}>
                        <span className="block overflow-hidden text-ellipsis">{factor}</span>
                    </Tooltip>
                ),
                mode: ruleModeControl(modeEditor, {
                    parameter: `factor:${factor}`,
                    enabled: record?.enabled === true,
                    value: record?.value ?? 10,
                    label: factor,
                    disabled,
                    onEnabledChange: (value) => actions.setFactorRuleEnabled(factor, value),
                }),
                limit: record?.enabled === true ? (
                    <AttachedLimit
                        editor={limitEditor()}
                        label={factor}
                        onRemove={() => actions.removeFactorRule(factor)}
                        disabled={disabled}
                    />
                ) : <NotSet />,
                baseline: (
                    <RuleValue
                        value={evaluation?.baseline ?? null}
                        unit={FACTOR_RULE.unit}
                        muted={ruleSwitchedOff(evaluation)}
                        tooltip={standaloneTooltip(evaluation, 'baseline')}
                    />
                ),
                standalone: (
                    <RuleValue
                        value={evaluation?.optimized ?? null}
                        unit={FACTOR_RULE.unit}
                        muted={ruleSwitchedOff(evaluation)}
                        tooltip={standaloneTooltip(evaluation, 'optimized')}
                    />
                ),
                track: (
                    <LimitTrack
                        evaluation={evaluation}
                        rule={FACTOR_RULE}
                        label={factor}
                        baselineLabel={baselineLabel}
                        draftLimit={draftLimit}
                    />
                ),
                status: (
                    <RuleStatusChip
                        evaluation={evaluation}
                        rule={FACTOR_RULE}
                        changed={changed}
                        stale={resultStale}
                    />
                ),
                contributionBaseline: (
                    <RuleValue
                        value={risk.act_factor_bps_euler ?? null}
                        unit="bps"
                        tooltip={contributionTooltip(risk.act_factor_risk_euler ?? null)}
                    />
                ),
                contribution: (
                    <RuleValue
                        value={risk.opt_factor_bps_euler ?? null}
                        unit="bps"
                        tooltip={contributionTooltip(risk.opt_factor_risk_euler ?? null)}
                    />
                ),
            },
        };
    });

    const busy = optimizerIsRunning || viewModeLoading;
    const isInitialLoading = Boolean(busy && rows.length === 0);

    return (
        <BusyOverlay show={busy && !isInitialLoading}>
            <DataTable
                aria-label="Factor constraints"
                data={rows}
                columns={columns}
                initialSorting={INITIAL_SORTING}
                loading={isInitialLoading}
                loadingRowCount={PLACEHOLDER_ROW_COUNT}
                headerHeight={RULE_TABLE_HEADER_HEIGHT}
                wrapHeaders
                className={RULE_TABLE_CLASS_NAME}
            />
        </BusyOverlay>
    );
}

ByFactorOptimizerRiskTable.propTypes = {
    factorNames: PropTypes.arrayOf(PropTypes.string).isRequired,
    risksByFactor: PropTypes.array,
    constraints: PropTypes.object.isRequired,
    evaluationIndex: PropTypes.shape({ byRuleId: PropTypes.instanceOf(Map) }).isRequired,
    baselineLabel: PropTypes.string.isRequired,
    actions: PropTypes.object.isRequired,
    resultStale: PropTypes.bool,
    disabled: PropTypes.bool,
    optimizerIsRunning: PropTypes.bool,
    viewModeLoading: PropTypes.bool,
};
